/**
 * useCurrentTask — Calcule en temps réel la tâche en cours, précédente et suivante.
 * Recalcule chaque minute (pas chaque seconde pour limiter les re-renders).
 */
import { useState, useEffect, useMemo, useCallback } from 'react';

/** Convertit "HH:MM" en minutes depuis minuit */
function timeToMin(timeStr) {
  if (!timeStr) return 0;
  const [h, m] = timeStr.split(':').map(Number);
  return h * 60 + m;
}

/** Retourne "YYYY-MM-DD" pour une date donnée (heure locale) */
function toDateStr(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * @param {object} opts
 * @param {Array}  opts.tasks          - toutes les tâches (useCustomTasks)
 * @param {Function} opts.isTaskCompleted - (dayIndex, task) → boolean
 * @param {number}  opts.currentDayIndex  - index du jour sélectionné (0=Lun…6=Dim)
 * @param {number}  opts.weekOffset       - décalage semaine (0 = semaine courante)
 */
export function useCurrentTask({ tasks, isTaskCompleted, currentDayIndex, weekOffset }) {
  // Tick toutes les 60 secondes pour recalculer
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  // Date d'aujourd'hui en string
  const today = useMemo(() => toDateStr(new Date()), [tick]); // eslint-disable-line

  // Tâches du jour triées par startTime
  const todayTasks = useMemo(() => {
    return (tasks || [])
      .filter(t => t.date === today)
      .sort((a, b) => timeToMin(a.startTime || a.time) - timeToMin(b.startTime || b.time));
  }, [tasks, today]);

  // Dériver les infos de temps et tâches courantes
  const derived = useMemo(() => {
    const now = new Date();
    const nowMin = now.getHours() * 60 + now.getMinutes();

    let currentTask = null;
    let currentIdx = -1;
    let nextTask = null;
    let previousTask = null;
    let timeRemainingInCurrent = 0;
    let progressPercent = 0;

    // Tâche en cours : celle dont la plage horaire contient nowMin
    for (let i = 0; i < todayTasks.length; i++) {
      const t = todayTasks[i];
      const start = timeToMin(t.startTime || t.time);
      const end = start + (t.duration || 30);
      if (nowMin >= start && nowMin < end) {
        currentTask = t;
        currentIdx = i;
        timeRemainingInCurrent = end - nowMin;
        progressPercent = Math.round(((nowMin - start) / (t.duration || 30)) * 100);
        break;
      }
    }

    // Si pas de tâche en cours, la prochaine non-complétée
    if (!currentTask) {
      for (let i = 0; i < todayTasks.length; i++) {
        const t = todayTasks[i];
        const start = timeToMin(t.startTime || t.time);
        if (start > nowMin && !isTaskCompleted(currentDayIndex, t)) {
          nextTask = t;
          // La "précédente" = dernière avant nowMin
          for (let j = i - 1; j >= 0; j--) {
            previousTask = todayTasks[j];
            break;
          }
          break;
        }
      }
      // Pas de prochaine → toutes passées
      if (!nextTask && todayTasks.length > 0) {
        previousTask = todayTasks[todayTasks.length - 1];
      }
    } else {
      // Tâche précédente = dernière avant currentIdx
      if (currentIdx > 0) previousTask = todayTasks[currentIdx - 1];
      // Tâche suivante = première non-complétée après currentIdx
      for (let i = currentIdx + 1; i < todayTasks.length; i++) {
        if (!isTaskCompleted(currentDayIndex, todayTasks[i])) {
          nextTask = todayTasks[i];
          break;
        }
      }
    }

    // Temps avant la prochaine tâche
    const timeUntilNext = nextTask
      ? Math.max(0, timeToMin(nextTask.startTime || nextTask.time) - nowMin)
      : null;

    // Nombre de tâches complétées aujourd'hui
    const completedToday = todayTasks.filter(t =>
      isTaskCompleted(currentDayIndex, t)
    ).length;

    // Statut global de la journée
    let dayStatus;
    if (todayTasks.length === 0) dayStatus = 'free';
    else if (currentTask) dayStatus = 'active';
    else if (nextTask) dayStatus = 'upcoming';
    else dayStatus = 'done';

    return {
      currentTask,
      previousTask,
      nextTask,
      timeRemainingInCurrent,
      progressPercent,
      timeUntilNext,
      completedToday,
      dayStatus,
      todayTasks,
      nowMin,
    };
  }, [todayTasks, isTaskCompleted, currentDayIndex, tick]); // eslint-disable-line

  return derived;
}

export default useCurrentTask;
